import React, { useMemo } from 'react';
import { Button } from '@skin-seoul/design-system';
import { CLASSNAME, Country, CountryCurrency, formatCurrency, getCouponDisplayName } from '@skin-seoul/service';
import type { CartCouponResponse } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';

interface PriceCalculatorProps {
  subtotal: number;
  deliveryFee: number;
  totalPrice: number;
  totalTax: number;
  coupons: CartCouponResponse[];
  isFreeShipping?: boolean;
  country: Country;
  itemCount?: number;
  isDetailsVisible: boolean;
  onDetailsVisibleChange: (visible: boolean) => void;
}

const CartOrderSummary = ({
  subtotal,
  deliveryFee,
  totalPrice,
  totalTax,
  coupons,
  isFreeShipping = false,
  country,
  itemCount,
  isDetailsVisible,
  onDetailsVisibleChange,
}: PriceCalculatorProps) => {
  const currency = CountryCurrency[country];
  const formattedSubtotal = formatCurrency(currency, subtotal);
  const formattedDeliveryFee = formatCurrency(currency, deliveryFee);
  const formattedTotalPrice = formatCurrency(currency, totalPrice);
  const formattedTotalTax = formatCurrency(currency, totalTax);

  const totalCouponDiscount = coupons.reduce((sum, c) => sum + c.discount, 0);
  const hasAnyFixedCartCoupon = coupons.some((c) => c.discountType === 'fixed_cart');
  const couponDiscountPercent =
    subtotal > 0 && totalCouponDiscount > 0 ? Math.round((totalCouponDiscount / subtotal) * 100) : 0;

  const displayCoupons = useMemo(
    () =>
      coupons.map((coupon) => {
        const isBmsmCoupon = coupon.code.toLowerCase().startsWith('bmsm');
        const discountPercent =
          coupon.discountType === 'percent' && subtotal > 0 && coupon.discount > 0
            ? Math.round((coupon.discount / subtotal) * 100)
            : 0;
        return {
          ...coupon,
          label: isBmsmCoupon ? 'BMSM Coupon' : getCouponDisplayName(coupon.code),
          discountPercent,
        };
      }),
    [coupons, subtotal],
  );

  const handleToggleDetails = () => {
    onDetailsVisibleChange(!isDetailsVisible);
  };

  return (
    <div className="flex flex-col gap-2">
      {isDetailsVisible && (
        <div className="flex flex-col gap-2 bg-primary-50 p-3 rounded-[8px]">
          <div className="flex items-center justify-between">
            <span className={`text-label-medium font-normal text-neutral-500 ${CLASSNAME.NOT_TRANSLATE}`}>
              Subtotal
            </span>
            <span className={`text-label-medium font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
              {formattedSubtotal}
            </span>
          </div>
          {displayCoupons.length > 0 &&
            displayCoupons.map((coupon) => (
              <div className="flex items-center justify-between" key={coupon.code}>
                <span className={`text-label-medium font-normal text-neutral-500 ${CLASSNAME.NOT_TRANSLATE}`}>
                  {coupon.label}
                  {coupon.discountPercent > 0 && (
                    <span className="text-label-medium font-medium text-primary-600">
                      {` (${coupon.discountPercent}% off)`}
                    </span>
                  )}
                </span>
                <span className={`text-label-medium font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                  -{formatCurrency(currency, coupon.discount)}
                </span>
              </div>
            ))}
          <div className="flex items-center justify-between">
            <span className={`text-label-medium font-normal text-neutral-500 ${CLASSNAME.NOT_TRANSLATE}`}>
              Delivery Fee
            </span>
            {isFreeShipping || deliveryFee === 0 ? (
              <span className={`text-label-medium font-medium text-neutral-700 uppercase ${CLASSNAME.NOT_TRANSLATE}`}>
                Free
              </span>
            ) : (
              <span className={`text-label-medium font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                {formattedDeliveryFee}
              </span>
            )}
          </div>
          <Condition
            expression={totalTax > 0}
            then={
              <div className="flex items-center justify-between">
                <span className={`text-label-medium font-normal text-neutral-500 ${CLASSNAME.NOT_TRANSLATE}`}>
                  Customs & taxes:
                </span>
                <span className={`text-label-medium font-medium text-neutral-700 ${CLASSNAME.NOT_TRANSLATE}`}>
                  {formattedTotalTax}
                </span>
              </div>
            }
          />
        </div>
      )}
      <div className="flex items-center justify-between gap-1">
        <div className="flex flex-col gap-1 flex-1 min-w-0 items-start">
          <span className={`text-label-medium font-medium text-neutral-800 ${CLASSNAME.NOT_TRANSLATE}`}>
            Order Total
            {itemCount != null && (
              <span className="text-label-small font-normal text-neutral-400"> ({itemCount} items)</span>
            )}
          </span>
          <Button
            variant="plain"
            size="xs"
            onClick={handleToggleDetails}
            className={`p-0 h-auto min-w-0 text-label-small font-normal text-neutral-400 underline ${CLASSNAME.NOT_TRANSLATE}`}
          >
            {isDetailsVisible ? 'Hide Details' : 'View Details'}
          </Button>
        </div>
        <div className="flex flex-col items-end gap-1 flex-1 min-w-0">
          <span className={`text-heading-h6-mobile font-semibold text-generic-100 ${CLASSNAME.NOT_TRANSLATE}`}>
            {formattedTotalPrice}
          </span>
          {totalCouponDiscount > 0 && (
            <div className="flex gap-1 items-center justify-end">
              <span className={`text-label-small font-normal text-neutral-400 line-through ${CLASSNAME.NOT_TRANSLATE}`}>
                {formattedSubtotal}
              </span>
              <span className={`text-label-small font-medium text-primary-600 ${CLASSNAME.NOT_TRANSLATE}`}>
                {hasAnyFixedCartCoupon
                  ? `-${formatCurrency(currency, totalCouponDiscount)}`
                  : `${couponDiscountPercent}% off`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartOrderSummary;
